/**
 * Abschnitt „Hersteller" im Admin-Bereich.
 *
 * Die Klammer über den Modellen: Ein Hersteller trägt nur seine Bezeichnung, alles Technische
 * hängt an seinen Modellen. Deshalb wird der Name direkt in der Tabelle geändert und nicht im
 * Dialog – ein Dialog für ein einziges Feld wäre ein Umweg.
 *
 * Die ausgewählte Zeile filtert den Abschnitt „Modelle" darunter.
 */
import React, { useCallback, useMemo, useRef } from "react";
import { Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Section from "../../../layout/Section";
import Button from "../../../common/inputs/Button";
import EditableTable, {
  EditableTableColumn,
  EditableTableRef,
} from "../../../tables/common/EditableTable";
import { EditableTableConfig } from "../../../tables/common/types";
import { useToast } from "../../../../contexts/ToastContext";
import { Hersteller } from "../../../../services/api/anlagentypen";

interface HerstellerSectionProps {
  hersteller: Hersteller[];
  loading: boolean;
  /** Der ausgewählte Hersteller filtert die Modelle darunter, `null` zeigt alle */
  ausgewaehlt: string | null;
  onAuswahl: (herstellerId: string) => void;
  onCreate: (name: string) => Promise<Hersteller>;
  onUpdate: (id: string, name: string) => Promise<Hersteller>;
  onDelete: (id: string) => Promise<void>;
}

const HerstellerSection: React.FC<HerstellerSectionProps> = ({
  hersteller,
  loading,
  ausgewaehlt,
  onAuswahl,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const { showToast } = useToast();
  const tableRef = useRef<EditableTableRef>(null);

  const columns: EditableTableColumn[] = useMemo(
    () => [
      {
        key: "name",
        label: "Bezeichnung",
        align: "left",
        editConfig: { type: "text", required: true },
      },
      {
        key: "anzahl_modelle",
        label: "Hinterlegte Modelle",
        align: "right",
        width: "200px",
      },
    ],
    [],
  );

  const editConfig: EditableTableConfig<Hersteller> = useMemo(
    () => ({
      fields: { name: { type: "text", required: true } },
      onCreate: async (daten) => {
        try {
          return await onCreate((daten.name ?? "").trim());
        } catch {
          showToast(
            "Der Hersteller konnte nicht angelegt werden – gibt es ihn schon?",
            "error",
          );
          throw new Error("create failed");
        }
      },
      onUpdate: async (id, aenderungen) => {
        try {
          return await onUpdate(id, (aenderungen.name ?? "").trim());
        } catch {
          showToast(
            "Der Hersteller konnte nicht umbenannt werden – gibt es den Namen schon?",
            "error",
          );
          throw new Error("update failed");
        }
      },
      onDelete: async (id) => {
        try {
          await onDelete(id);
          showToast("Hersteller gelöscht", "success");
        } catch {
          showToast(
            "Zum Hersteller gibt es noch Modelle. Erst die Modelle löschen, dann den Hersteller.",
            "error",
          );
          throw new Error("delete failed");
        }
      },
    }),
    [onCreate, onUpdate, onDelete, showToast],
  );

  // Die Anzahl ist abgeleitet und nie editierbar – sie zählt die Zeilen der Tabelle darunter.
  const renderCell = useCallback(
    (spalte: EditableTableColumn, _zeile: Hersteller, wert: unknown) => {
      if (spalte.key !== "anzahl_modelle") return undefined;
      return (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {(wert as number) ?? 0}
        </Typography>
      );
    },
    [],
  );

  return (
    <Section
      title="Hersteller"
      noPadding
      actions={
        <Button
          startIcon={<AddIcon />}
          onClick={() => tableRef.current?.startCreating({ name: "" })}
        >
          Hersteller anlegen
        </Button>
      }
    >
      <EditableTable<Hersteller>
        ref={tableRef}
        columns={columns}
        data={hersteller}
        editConfig={editConfig}
        canEdit
        renderCell={renderCell}
        onRowSelect={(zeile) => onAuswahl(zeile.id)}
        selectedRowId={ausgewaehlt ?? undefined}
        loading={loading}
        showBorder={false}
        actionsColumnWidth="60px"
        emptyMessage="Noch keine Hersteller erfasst."
      />
    </Section>
  );
};

export default HerstellerSection;
